<script setup>
import { computed, ref } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Tendencia Semanal',
  },
});

const chartContainer = ref(null);
const tooltip = ref({
  show: false,
  x: 0,
  y: 0,
  date: '',
  created: 0,
  resolved: 0,
  messages: 0,
});
const activeSeries = ref(['created', 'resolved', 'messages']);

// Chart dimensions
const chartWidth = 900;
const chartHeight = 280;
const padding = { top: 30, right: 20, bottom: 50, left: 50 };

// Series configuration
const seriesConfig = {
  created: { color: '#3b82f6', label: 'Criadas' },
  resolved: { color: '#22c55e', label: 'Resolvidas' },
  messages: { color: '#a855f7', label: 'Mensagens' },
};

// Helper to safely extract numeric value
const safeNumber = val => {
  const num = Number(val);
  return Number.isFinite(num) ? num : 0;
};

// Process data for chart
const chartData = computed(() => {
  if (!props.data || props.data.length === 0) return [];
  return props.data
    .map(d => {
      let date = new Date(d.date);
      if (isNaN(date.getTime())) {
        date = new Date();
      }
      return {
        date,
        created: safeNumber(d.created),
        resolved: safeNumber(d.resolved),
        messages: safeNumber(d.messages),
      };
    })
    .sort((a, b) => a.date - b.date);
});

// Calculate max value across all active series
const maxValue = computed(() => {
  if (chartData.value.length === 0) return 1;
  let max = 0;
  chartData.value.forEach(d => {
    activeSeries.value.forEach(series => {
      if (d[series] > max) max = d[series];
    });
  });
  return max || 1;
});

// X scale
const xScale = computed(() => {
  if (chartData.value.length === 0) return () => padding.left;
  const dates = chartData.value.map(d => d.date.getTime());
  const min = Math.min(...dates);
  const max = Math.max(...dates);
  const range = max - min || 1;
  return date => {
    const x =
      padding.left +
      ((date.getTime() - min) / range) *
        (chartWidth - padding.left - padding.right);
    return Number.isFinite(x) ? x : padding.left;
  };
});

// Y scale
const yScale = computed(() => {
  const defaultY = chartHeight - padding.bottom;
  return value => {
    const safeValue = safeNumber(value);
    const y =
      chartHeight -
      padding.bottom -
      (safeValue / maxValue.value) *
        (chartHeight - padding.top - padding.bottom);
    return Number.isFinite(y) ? y : defaultY;
  };
});

// Generate line paths for each series
function generateLinePath(seriesKey) {
  if (chartData.value.length === 0) return '';
  return chartData.value
    .map((d, i) => {
      const x = xScale.value(d.date);
      const y = yScale.value(d[seriesKey]);
      return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
    })
    .join(' ');
}

// Y-axis ticks
const yTicks = computed(() => {
  const ticks = [];
  const step = Math.ceil(maxValue.value / 5);
  for (let i = 0; i <= maxValue.value; i += step) {
    ticks.push({
      value: i,
      y: yScale.value(i),
    });
  }
  return ticks;
});

// X-axis labels
const xLabels = computed(() => {
  if (chartData.value.length === 0) return [];
  const step = Math.max(1, Math.ceil(chartData.value.length / 10));
  return chartData.value
    .filter((_, i) => i % step === 0)
    .map(d => ({
      date: d.date,
      label: `${String(d.date.getDate()).padStart(2, '0')}/${String(d.date.getMonth() + 1).padStart(2, '0')}`,
      x: xScale.value(d.date),
    }));
});

function toggleSeries(series) {
  const index = activeSeries.value.indexOf(series);
  if (index === -1) {
    activeSeries.value.push(series);
  } else if (activeSeries.value.length > 1) {
    activeSeries.value.splice(index, 1);
  }
}

function showTooltip(event, data) {
  const rect = chartContainer.value?.getBoundingClientRect();
  if (!rect) return;

  tooltip.value = {
    show: true,
    x: event.clientX - rect.left,
    y: event.clientY - rect.top - 10,
    date: `${String(data.date.getDate()).padStart(2, '0')}/${String(data.date.getMonth() + 1).padStart(2, '0')}`,
    created: data.created,
    resolved: data.resolved,
    messages: data.messages,
  };
}

function hideTooltip() {
  tooltip.value.show = false;
}
</script>

<template>
  <div
    ref="chartContainer"
    class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4"
  >
    <!-- Header with legend -->
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-2">
        <span class="i-lucide-trending-up text-lg text-n-blue-10" />
        <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
      </div>
      <div class="flex items-center gap-4">
        <button
          v-for="(config, key) in seriesConfig"
          :key="key"
          class="flex items-center gap-1.5 text-xs transition-opacity"
          :class="activeSeries.includes(key) ? 'opacity-100' : 'opacity-40'"
          @click="toggleSeries(key)"
        >
          <span
            class="w-3 h-3 rounded-full"
            :style="{ backgroundColor: config.color }"
          />
          <span class="text-n-slate-11">{{ config.label }}</span>
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-72">
      <Spinner size="large" />
    </div>

    <!-- Empty State -->
    <div
      v-else-if="chartData.length === 0"
      class="flex items-center justify-center h-72 text-n-slate-11"
    >
      <span class="text-sm">Sem dados disponiveis</span>
    </div>

    <!-- Chart -->
    <div v-else class="relative">
      <svg
        :viewBox="`0 0 ${chartWidth} ${chartHeight}`"
        class="w-full h-72"
        preserveAspectRatio="xMidYMid meet"
      >
        <!-- Grid lines -->
        <g class="grid-lines">
          <line
            v-for="tick in yTicks"
            :key="tick.value"
            :x1="padding.left"
            :y1="tick.y"
            :x2="chartWidth - padding.right"
            :y2="tick.y"
            stroke="currentColor"
            stroke-opacity="0.1"
            stroke-dasharray="4"
          />
        </g>

        <!-- Y-axis labels -->
        <g class="y-axis">
          <text
            v-for="tick in yTicks"
            :key="tick.value"
            :x="padding.left - 10"
            :y="tick.y + 4"
            text-anchor="end"
            class="fill-n-slate-10 text-xs"
          >
            {{ tick.value }}
          </text>
        </g>

        <!-- X-axis labels -->
        <g class="x-axis">
          <text
            v-for="label in xLabels"
            :key="label.x"
            :x="label.x"
            :y="chartHeight - 15"
            text-anchor="middle"
            class="fill-n-slate-10 text-xs"
          >
            {{ label.label }}
          </text>
        </g>

        <!-- Lines for each active series -->
        <g v-for="series in activeSeries" :key="series">
          <path
            :d="generateLinePath(series)"
            fill="none"
            :stroke="seriesConfig[series].color"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </g>

        <!-- Data points (hover targets) -->
        <g class="data-points">
          <circle
            v-for="(d, i) in chartData"
            :key="i"
            :cx="xScale(d.date)"
            :cy="yScale(d.created)"
            r="15"
            fill="transparent"
            class="cursor-pointer"
            @mouseenter="showTooltip($event, d)"
            @mouseleave="hideTooltip"
          />
        </g>
      </svg>

      <!-- Tooltip -->
      <div
        v-if="tooltip.show"
        class="absolute pointer-events-none px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak shadow-lg text-xs z-10"
        :style="{
          left: `${tooltip.x}px`,
          top: `${tooltip.y}px`,
          transform: 'translate(-50%, -100%)',
        }"
      >
        <div class="font-medium text-n-slate-12 mb-1">{{ tooltip.date }}</div>
        <div class="flex items-center gap-2">
          <span
            class="w-2 h-2 rounded-full"
            :style="{ backgroundColor: seriesConfig.created.color }"
          />
          <span class="text-n-slate-11">Criadas:</span>
          <span class="font-medium text-n-slate-12">{{ tooltip.created }}</span>
        </div>
        <div class="flex items-center gap-2">
          <span
            class="w-2 h-2 rounded-full"
            :style="{ backgroundColor: seriesConfig.resolved.color }"
          />
          <span class="text-n-slate-11">Resolvidas:</span>
          <span class="font-medium text-n-slate-12">{{
            tooltip.resolved
          }}</span>
        </div>
        <div class="flex items-center gap-2">
          <span
            class="w-2 h-2 rounded-full"
            :style="{ backgroundColor: seriesConfig.messages.color }"
          />
          <span class="text-n-slate-11">Mensagens:</span>
          <span class="font-medium text-n-slate-12">{{
            tooltip.messages
          }}</span>
        </div>
      </div>
    </div>
  </div>
</template>
